import type { DashboardAssistantEvent, DashboardEditOperation } from "@t3tools/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { SparklesIcon } from "lucide-react";
import { useCallback, useMemo, useState, type ReactNode } from "react";

import { useAccess } from "~/access/AccessProvider";
import { Button } from "~/components/ui/button";
import { toastManager } from "~/components/ui/toast";
import { useIsMobile } from "~/hooks/useMediaQuery";

import { DASHBOARDS_ARTIFACTS_PUBLISH_PERMISSION, canEditArtifact } from "../ArtifactActions";
import {
  DashboardArtifactRequestError,
  dashboardArtifactQueryOptions,
  dashboardArtifactsQueryKeys,
  postDashboardArtifactEdits,
} from "../artifactsReactQuery";
import { renameDashboardOps, validDirectEditOps, type DirectEdit } from "../directEdits";
import type { DashboardArtifactSummary } from "../artifactTypes";
import { DashboardAssistantLayout, useAssistantComposerShortcut } from "./DashboardAssistantLayout";
import { DashboardAssistantPanel } from "./DashboardAssistantPanel";
import type { ArtifactSpec } from "../artifactTypes";
import type { DashboardAssistantSource } from "./dashboardAssistantSource";
import { dashboardAssistantPrompts } from "./dashboardAssistantPrompts";
import { editedWidgetIds } from "./dashboardAssistantThread";
import { DashboardUndoError, undoDashboardAssistantEdits } from "./dashboardAssistantUndo";
import {
  useDashboardAssistantThread,
  useDashboardAssistantThreadId,
} from "./useDashboardAssistantThread";
import { useDashboardLiveEdits } from "./useDashboardLiveEdits";

const COMPOSER_ID = "dashboard-assistant-composer";
const ASK_BUTTON_ID = "dashboard-assistant-ask";

export type ArtifactDashboardSlots = {
  /** The Ask button, for the dashboard's header actions. */
  askButton: ReactNode;
  highlightedWidgetIds: ReadonlySet<string>;
  /** Direct edits for each widget; undefined when this version can't be edited. */
  widgetEditor:
    | {
        busy: boolean;
        onEdit: (widgetId: string, edit: DirectEdit) => void;
        renameDashboard: (title: string) => void;
      }
    | undefined;
  /** What an empty dashboard says when its last build failed; undefined otherwise. */
  emptyNotice: ReactNode;
  previews: ReturnType<typeof useDashboardLiveEdits>["previews"];
};

/**
 * A dashboard with its assistant beside it. Owners and publishers get an
 * assistant that edits (open by default); everyone else can ask questions
 * (closed by default). Saved edits show in place: the page switches to the
 * new version without a reload and highlights the widgets that changed.
 */
export function ArtifactAssistantWorkspace(props: {
  artifact: DashboardArtifactSummary;
  /** Renders the dashboard with the assistant's live state. */
  renderDashboard: (slots: ArtifactDashboardSlots) => ReactNode;
  /** Loads `version` and shows it in place. */
  showVersion: (version: number) => Promise<void>;
  source: DashboardAssistantSource;
  spec: ArtifactSpec;
  /** The version on screen; direct edits apply only to the latest one. */
  shownVersion: number;
  versionHref: (version: number) => string;
  viewerEmail: string | null;
}) {
  const { artifact, showVersion } = props;
  const access = useAccess();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const mode = canEditArtifact({
    artifact,
    canPublish: access.can(DASHBOARDS_ARTIFACTS_PUBLISH_PERMISSION),
    viewerEmail: props.viewerEmail,
  })
    ? "edit"
    : "ask";
  const [open, setOpen] = useState(mode === "edit" && !isMobile);
  const threadId = useDashboardAssistantThreadId(artifact.id);
  const live = useDashboardLiveEdits();
  const { highlight, preview, clearPreviews } = live;

  const applyVersion = useCallback(
    async (version: number, widgetIds: ReadonlySet<string>) => {
      try {
        await showVersion(version);
        clearPreviews(widgetIds);
        highlight(widgetIds);
      } catch (error) {
        toastManager.add({
          description: error instanceof Error ? error.message : String(error),
          title: `Version ${version} was saved but could not load`,
          type: "error",
        });
      }
    },
    [showVersion, clearPreviews, highlight],
  );

  const onEvent = useCallback(
    (event: DashboardAssistantEvent) => {
      if (event.type === "preview") preview(event.widgetId, event);
      if (
        event.type === "edits.applied" &&
        [artifact.slug, artifact.id].includes(event.dashboardRef)
      ) {
        void applyVersion(event.version, editedWidgetIds(event.ops));
      }
    },
    [artifact.id, artifact.slug, applyVersion, preview],
  );

  const [editing, setEditing] = useState(false);
  const editable = mode === "edit" && props.shownVersion === artifact.latestVersion;
  const undoDirectEdit = useCallback(
    async (version: number, ops: ReadonlyArray<DashboardEditOperation>) => {
      try {
        const result = await undoDashboardAssistantEdits({
          loadSpec: async (target) =>
            (await queryClient.fetchQuery(dashboardArtifactQueryOptions(artifact.slug, target)))
              .spec,
          ops,
          ref: artifact.slug,
          version,
        });
        await queryClient.invalidateQueries({ queryKey: dashboardArtifactsQueryKeys.all });
        await applyVersion(result.version.version, editedWidgetIds(ops));
      } catch (error) {
        toastManager.add({
          description:
            error instanceof DashboardUndoError
              ? error.message
              : "The undo didn't save. Try again in a moment.",
          title: "Not undone",
          type: "error",
        });
      }
    },
    [artifact.slug, queryClient, applyVersion],
  );
  /** Saves direct edits as a new version, shows them, and offers Undo. */
  const saveEdit = useCallback(
    async (ops: ReadonlyArray<DashboardEditOperation>, toastTitle: string) => {
      setEditing(true);
      try {
        const result = await postDashboardArtifactEdits(artifact.slug, {
          baseVersion: props.shownVersion,
          ops,
        });
        await queryClient.invalidateQueries({ queryKey: dashboardArtifactsQueryKeys.all });
        await applyVersion(result.version.version, editedWidgetIds(ops));
        toastManager.add({
          actionProps: {
            children: "Undo",
            onClick: () => void undoDirectEdit(result.version.version, ops),
          },
          timeout: 8_000,
          title: toastTitle,
          type: "success",
        });
      } catch (error) {
        toastManager.add({
          description:
            error instanceof DashboardArtifactRequestError && error.status === 409
              ? "The dashboard changed since you opened it. Reload to see the latest version."
              : error instanceof Error
                ? error.message
                : String(error),
          title: "The change didn't save",
          type: "error",
        });
      } finally {
        setEditing(false);
      }
    },
    [artifact.slug, props.shownVersion, queryClient, applyVersion, undoDirectEdit],
  );
  const editWidget = useCallback(
    async (widgetId: string, edit: DirectEdit) => {
      const ops = validDirectEditOps(props.spec, widgetId, edit);
      if (!ops) {
        toastManager.add({ title: "That change isn't possible for this widget", type: "error" });
        return;
      }
      await saveEdit(ops, edit.kind === "remove" ? "Widget removed" : "Saved");
    },
    [props.spec, saveEdit],
  );
  const renameDashboard = useCallback(
    async (title: string) => {
      const ops = renameDashboardOps(props.spec, title);
      if (ops) await saveEdit(ops, "Dashboard renamed");
    },
    [props.spec, saveEdit],
  );

  const thread = useDashboardAssistantThread({
    source: props.source,
    dashboardRef: artifact.slug,
    threadId,
    onEvent,
  });
  const { dispatch } = thread;

  const openPanel = useCallback(() => setOpen(true), []);
  useAssistantComposerShortcut({ composerId: COMPOSER_ID, enabled: true, onOpen: openPanel });

  const prompts = useMemo(() => dashboardAssistantPrompts(props.spec, mode), [props.spec, mode]);
  const panelTitle = mode === "edit" ? "Assistant" : "Ask a question";

  const panel = (close: () => void) => (
    <DashboardAssistantPanel
      composerId={COMPOSER_ID}
      mode={mode}
      onCancel={thread.cancel}
      onClose={close}
      onOpenVersion={(version) => void showVersion(version)}
      onSend={(text) => {
        clearPreviews();
        thread.send(text);
      }}
      onUndo={(item) => {
        dispatch({ type: "undo", itemId: item.id, undo: { state: "pending" } });
        undoDashboardAssistantEdits({
          ref: item.dashboardRef,
          version: item.version,
          ops: item.ops,
          loadSpec: async (version) =>
            (await queryClient.fetchQuery(dashboardArtifactQueryOptions(artifact.slug, version)))
              .spec,
        })
          .then(async (result) => {
            dispatch({
              type: "undo",
              itemId: item.id,
              undo: { state: "done", version: result.version.version },
            });
            await applyVersion(result.version.version, editedWidgetIds(item.ops));
          })
          .catch((error: unknown) =>
            dispatch({
              type: "undo",
              itemId: item.id,
              undo: {
                state: "failed",
                message:
                  error instanceof DashboardUndoError
                    ? error.message
                    : "The undo didn't save. Try again in a moment.",
              },
            }),
          );
      }}
      prompts={prompts}
      state={thread.state}
      versionHref={props.versionHref}
    />
  );

  const lastItem = thread.state.items.at(-1);
  const lastQuestion = [...thread.state.items].reverse().find((item) => item.kind === "user");
  const failedBuild =
    mode === "edit" &&
    props.spec.rows.length === 0 &&
    thread.state.running === null &&
    lastItem?.kind === "error" &&
    lastQuestion?.kind === "user"
      ? { reason: lastItem.plain, question: lastQuestion.text }
      : null;
  const emptyNotice = failedBuild ? (
    <div className="space-y-3" data-artifact-build-failed="">
      <p className="text-sm font-medium text-foreground">That build didn't finish</p>
      <p className="mx-auto max-w-md text-sm text-muted-foreground">{failedBuild.reason}</p>
      <p className="mx-auto max-w-md text-xs text-muted-foreground">
        You asked: "{failedBuild.question}"
      </p>
      <Button onClick={() => thread.send(failedBuild.question)} size="sm">
        Try again
      </Button>
    </div>
  ) : undefined;

  const askButton =
    !open || isMobile ? (
      <Button
        aria-expanded={open}
        id={ASK_BUTTON_ID}
        onClick={() => setOpen(true)}
        size="sm"
        variant="outline"
      >
        <SparklesIcon aria-hidden />
        {mode === "edit" ? "Ask" : "Ask a question"}
      </Button>
    ) : null;

  return (
    <DashboardAssistantLayout
      onOpenChange={setOpen}
      open={open}
      panel={panel}
      panelTitle={panelTitle}
      returnFocusId={ASK_BUTTON_ID}
    >
      {props.renderDashboard({
        askButton,
        emptyNotice,
        widgetEditor: editable
          ? {
              busy: editing,
              onEdit: (id, edit) => void editWidget(id, edit),
              renameDashboard: (title) => void renameDashboard(title),
            }
          : undefined,
        highlightedWidgetIds: live.highlighted,
        previews: live.previews,
      })}
    </DashboardAssistantLayout>
  );
}
